import { useAuthentication } from '@/composables/auth/useAuthentication';
import CollaboratorProfilesPage from '@/pages/_sales/CollaboratorProfilesPage/CollaboratorProfilesPage.vue';
// Use a relaxed navigation item type to accept both API and static submenu nodes
import type { SubmenuItem } from '@/types/menu';
type SubmenuLike = { id: number | string; uuid?: string; type?: string; link?: string };
import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router';
import { useConfig } from './composables/useConfig';
import { useLogger } from '@/composables/useLogger';
import i18n from '@/i18n';

const { logger } = useLogger();

const LoginLayout = () => import('./layouts/LoginLayout.vue');
const AppLayout = () => import('./layouts/AppLayout.vue');
const HomePage = () => import('./pages/HomePage/HomePage.vue');
const SpreadsheetPage = () => import('./pages/SpreadsheetPage/SpreadsheetPage.vue');
const ProjectListPage = () => import('@/pages/_projects/ProjectListPage/ProjectListPage.vue');
const CollaboratorProfitPage = () => import('@/pages/_finance/CollaboratorProfitPage/CollaboratorProfitPage.vue');
const InvoiceListPage = () => import('@/pages/_finance/InvoiceListPage/InvoiceListPage.vue');
const OrganizationListPage = () => import('@/pages/_sales/OrganizationListPage/OrganizationListPage.vue');
const BankTransactionListPageV2 = () =>
  import('@/pages/_finance/BankTransactionListPageV2/BankTransactionListPageV2.vue');
const GoogleDocLayout = () => import('./layouts/GoogleDocLayout.vue');
const WorkLogAnalysisPage = () => import('@/pages/_analysis/WorkLogAnalysisPage/WorkLogAnalysisPage.vue');
const CostCategoryAnalysisPage = () =>
  import('@/pages/_analysis/CostCategoryAnalysisPage/CostCategoryAnalysisPage.vue');
const EstimateAnalysisPage = () => import('@/pages/_projects/EstimateAnalysisPage/EstimateAnalysisPage.vue');
const WorkLogIssueHelp = () => import('./components/WorkLogIssueComponents/WorkLogIssueHelp.vue');
const CollaboratorInternalListPage = () =>
  import('@/pages/_hr/CollaboratorInternalListPage/CollaboratorInternalListPage.vue');
const CollaboratorExternalListPage = () =>
  import('@/pages/_hr/CollaboratorExternalListPage/CollaboratorExternalListPage.vue');
const PersonalSettingsPage = () => import('./pages/PersonalSettingsPage/PersonalSettingsPage.vue');
const CollaboratorWorkPlanPageV2 = () =>
  import('@/pages/_planning/CollaboratorWorkPlanPageV2/CollaboratorWorkPlanPageV2.vue');
const CollaboratorProjectPlanPageV2 = () =>
  import('@/pages/_planning/CollaboratorProjectPlanPageV2/CollaboratorProjectPlanPageV2.vue');
const ProjectPlanHelp = () => import('./components/ProjectPlanComponents/ProjectPlanHelp.vue');
const CollaboratorInternalCostListPage = () =>
  import('@/pages/_finance/CollaboratorInternalCostListPage/CollaboratorInternalCostListPage.vue');
const CollaboratorExternalCostListPage = () =>
  import('@/pages/_finance/CollaboratorExternalCostListPage/CollaboratorExternalCostListPage.vue');
const CostsPlanningPage = () => import('@/pages/_finance/CostsPlanningPage/CostsPlanningPage.vue');
const CompanyProfitPage = () => import('@/pages/_finance/CompanyProfitPage/CompanyProfitPage.vue');
const CollaboratorSkillsPage = () => import('@/pages/_hr/CollaboratorSkillsPage/CollaboratorSkillsPage.vue');
const AssetListPage = () => import('@/pages/_finance/AssetListPage/AssetListPage.vue');
const ProjectCalculationPage = () => import('@/pages/_projects/ProjectCalculationPage/ProjectCalculationPage.vue');
const ProjectOverviewPage = () => import('@/pages/_projects/ProjectOverviewPage/ProjectOverviewPage.vue');
const OrderListPage = () => import('@/pages/_sales/OrderListPage/OrderListPage.vue');
const AvailableCapacityReportPageV3 = () =>
  import('@/pages/_sales/AvailableCapacityReportPageV3/AvailableCapacityReportPageV3.vue');
const HomePageHelp = () => import('./components/HomePageComponents/HomePageHelp.vue');
const ProjectOverallCalculationPage = () =>
  import('@/pages/_finance/ProjectOverallCalculationPage/ProjectOverallCalculationPage.vue');
const CompanyProfitAnalysisPage = () =>
  import('@/pages/_analysis/CompanyProfitAnalysisPage/CompanyProfitAnalysisPage.vue');
const OrderRoadmapPage = () => import('@/pages/_sales/OrderRoadmapPage/OrderRoadmapPage.vue');
const SalesProjectsStatisticsPage = () =>
  import('@/pages/_sales/SalesProjectsStatisticsPage/SalesProjectsStatisticsPage.vue');
const WorklogTimesheetPage = () => import('@/pages/_projects/WorklogTimesheetPage/WorklogTimesheetPage.vue');
const AiChatV2Page = () => import('@/pages/AiChatV2Page/AiChatV2Page.vue');
const CollaboratorStatsPage = () => import('@/pages/_hr/CollaboratorStatsPage/CollaboratorStatsPage.vue');
const wrapperComponent = { template: '<router-view></router-view>' };

export const ORDERS_ROUTE = '/app/sales/orders';

export const routes: Array<RouteRecordRaw> = [
  { path: '/', component: LoginLayout },
  {
    path: '/google-doc',
    component: GoogleDocLayout,
    children: [
      {
        path: 'document/:id',
        component: SpreadsheetPage,
      },
    ],
  },
  {
    path: '/app',
    component: AppLayout,
    children: [
      {
        path: 'home',
        name: 'Home',
        component: HomePage,
        meta: {
          helpComponent: HomePageHelp,
          key: 'home',
        },
      },
      {
        path: 'personal-settings',
        name: 'Personal Settings',
        component: PersonalSettingsPage,
        meta: { key: 'personalSettings' },
      },
      {
        path: 'ai-chat-v2',
        name: 'AI Chat v2',
        component: AiChatV2Page,
        meta: { key: 'aiChatV2' },
      },
      {
        path: 'sales',
        name: 'Sales',
        component: wrapperComponent,
        meta: { key: 'sales' },
        children: [
          {
            path: 'organizations',
            name: 'Organizations',
            component: OrganizationListPage,
            meta: {
              permission: {
                subject: 'ERP_ORGANIZATIONS',
                action: 'READ',
              },
              key: 'organizations',
            },
          },
          {
            path: 'orders',
            name: 'Orders',
            component: OrderListPage,
            meta: {
              permission: {
                subject: 'IOS_ORDERS',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'orders',
            },
          },
          {
            path: 'collaborator-capacity-report-v3',
            name: 'Available capacity report',
            component: AvailableCapacityReportPageV3,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATOR_CAPACITY_REPORT',
                action: 'READ',
              },
              key: 'availableCapacityReport',
            },
          },
          {
            path: 'orders-roadmap',
            name: 'Orders roadmap',
            component: OrderRoadmapPage,
            meta: {
              permission: {
                subject: 'IOS_ORDERS',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'ordersRoadmap',
            },
          },
          {
            path: 'sales-projects-statistics/month/:month',
            name: 'Sales projects statistics',
            component: SalesProjectsStatisticsPage,
            meta: {
              permission: {
                subject: 'ERP_SALES_PROJECTS_STATISTICS',
                action: 'READ',
              },
              key: 'salesProjectsStatistics',
            },
          },
          {
            path: 'collaborator-profiles',
            name: 'Collaborator Profiles',
            component: CollaboratorProfilesPage,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATOR_PROFILES',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'collaboratorProfiles',
            },
          },
        ],
      },
      {
        path: 'projects',
        name: 'Projects',
        component: wrapperComponent,
        meta: { key: 'projects' },
        children: [
          {
            path: 'project-list',
            name: 'Projects List',
            component: ProjectListPage,
            meta: {
              permission: {
                subject: 'ERP_PROJECTS',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'projectsList',
            },
          },
          {
            path: 'project-calculation/project/:project/month/:month',
            name: 'Project Calculation',
            component: ProjectCalculationPage,
            meta: {
              permission: {
                subject: 'ERP_PROJECT_CALCULATIONS',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'projectCalculation',
            },
          },
          {
            path: 'project-overview/project/:project',
            name: 'Project Overview',
            component: ProjectOverviewPage,
            meta: {
              permission: {
                subject: 'ERP_PROJECTS',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'projectOverview',
            },
          },
          {
            path: 'worklog-timesheet',
            name: 'Worklog timesheet',
            component: WorklogTimesheetPage,
            meta: {
              permission: {
                subject: 'ERP_WORKLOG_TIMESHEETS',
                action: ['READ', 'READ_FOR_RESPONSIBLE_PERSON'],
              },
              key: 'worklogTimesheet',
            },
          },
          {
            path: 'estimate-analysis',
            name: 'Estimate analysis',
            component: EstimateAnalysisPage,
            meta: {
              permission: {
                subject: 'TIS_ESTIMATES_ANALYSIS',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'estimateAnalysis',
            },
          },
        ],
      },
      {
        path: 'HR',
        name: 'HR',
        component: wrapperComponent,
        meta: { key: 'hr' },
        children: [
          {
            path: 'collaborators-internal',
            name: 'Internal Collaborators',
            component: CollaboratorInternalListPage,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATORS_INTERNAL',
                action: 'READ_ALL_PROPERTIES',
              },
              key: 'collaboratorsInternal',
            },
          },
          {
            path: 'collaborators-external',
            name: 'External Collaborators',
            component: CollaboratorExternalListPage,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATORS_EXTERNAL',
                action: 'READ_ALL_PROPERTIES',
              },
              key: 'collaboratorsExternal',
            },
          },
          {
            path: 'collaborator-skills',
            name: 'Collaborators skills',
            component: CollaboratorSkillsPage,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATOR_SKILLS',
                action: 'READ',
              },
              key: 'collaboratorSkills',
            },
          },
          {
            path: 'collaborator-stats',
            name: 'Collaborators statistics',
            component: CollaboratorStatsPage,
            meta: {
              permission: {
                subject: ['ERP_COLLABORATORS_INTERNAL', 'ERP_COLLABORATORS_EXTERNAL'],
                action: 'READ_ALL_PROPERTIES',
                logic: 'OR',
              },
              key: 'collaboratorStats',
            },
          },
        ],
      },
      {
        path: 'finance',
        name: 'Finance',
        component: wrapperComponent,
        meta: { key: 'finance' },
        children: [
          {
            path: 'bank-transactions',
            name: 'Bank transactions',
            component: BankTransactionListPageV2,
            meta: {
              permission: {
                subject: 'FNS_BANK_TRANSACTIONS',
                action: 'READ',
              },
              key: 'bankTransactionsV2',
            },
          },
          {
            path: 'collaborator-costs-internal',
            name: 'Internal Collaborator costs',
            component: CollaboratorInternalCostListPage,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATOR_INTERNAL_COST',
                action: ['READ', 'READ_FOR_RESPONSIBLE_PERSON'],
              },
              key: 'collaboratorCostsInternal',
            },
          },
          {
            path: 'collaborator-costs-external',
            name: 'External Collaborator costs',
            component: CollaboratorExternalCostListPage,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATOR_EXTERNAL_COST',
                action: ['READ', 'READ_FOR_RESPONSIBLE_PERSON'],
              },
              key: 'collaboratorCostsExternal',
            },
          },
          {
            path: 'costs-planning',
            name: 'Costs planning',
            component: CostsPlanningPage,
            meta: {
              permission: {
                subject: 'ERP_OPERATING_COSTS',
                action: 'READ',
              },
              key: 'costsPlanning',
            },
          },
          {
            path: 'company-profit',
            name: 'Company profit',
            component: CompanyProfitPage,
            meta: {
              permission: {
                subject: 'ERP_COMPANY_PROFIT',
                action: 'READ',
              },
              key: 'companyProfit',
            },
          },
          {
            path: 'invoices',
            name: 'Invoices',
            component: InvoiceListPage,
            meta: {
              permission: {
                subject: 'IOS_INVOICES',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'invoices',
            },
          },
          {
            path: 'asset-list',
            name: 'Assets',
            component: AssetListPage,
            meta: {
              permission: {
                subject: 'ASS_ASSETS',
                action: 'READ',
              },
              key: 'assets',
            },
          },
          {
            path: 'project-overall-calculation',
            name: 'Project overall calculation',
            component: ProjectOverallCalculationPage,
            meta: {
              permission: {
                subject: 'ERP_PROJECT_CALCULATIONS',
                action: ['READ_OWN', 'READ_ALL'],
              },
              key: 'projectOverallCalculation',
            },
          },
          {
            path: 'collaborator-profit',
            name: 'Collaborator profit',
            component: CollaboratorProfitPage,
            meta: {
              permission: {
                subject: 'COLLABORATOR_PROFIT',
                action: ['VIEW_ALL_INTERNAL', 'VIEW_ALL_EXTERNAL'],
              },
              key: 'collaboratorProfit',
            },
          },
        ],
      },
      {
        path: 'analysis',
        name: 'Analysis',
        component: wrapperComponent,
        meta: { key: 'analysis' },
        children: [
          {
            path: 'worklog-analysis',
            name: 'WorkLog analysis',
            component: WorkLogAnalysisPage,
            meta: {
              permission: {
                subject: 'ERP_WORKLOG_ANALYSIS',
                action: 'READ',
              },
              helpComponent: WorkLogIssueHelp,
              key: 'worklogAnalysis',
            },
          },
          {
            path: 'costs-analysis',
            name: 'Costs analysis',
            component: CostCategoryAnalysisPage,
            meta: {
              permission: {
                subject: 'ERP_COSTS_ANALYSIS',
                action: 'READ',
              },
              key: 'costsAnalysis',
            },
          },
          {
            path: 'company-profit-analysis/:year',
            name: 'Company profit analysis',
            component: CompanyProfitAnalysisPage,
            meta: {
              permission: {
                subject: 'ERP_COMPANY_PROFIT_ANALYSIS',
                action: 'READ',
              },
              key: 'companyProfitAnalysis',
            },
          },
        ],
      },
      {
        path: 'planning',
        name: 'Planning',
        component: wrapperComponent,
        meta: { key: 'planning' },
        children: [
          {
            path: 'collaborator-work-plan',
            name: 'Collaborator work plan',
            component: CollaboratorWorkPlanPageV2,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATOR_WORK_PLAN',
                action: 'READ',
              },
              key: 'collaboratorWorkPlan',
            },
          },
          {
            path: 'collaborator-project-plan-v2',
            name: 'Collaborator project plan',
            component: CollaboratorProjectPlanPageV2,
            meta: {
              permission: {
                subject: 'ERP_COLLABORATOR_PROJECT_PLAN',
                action: 'READ',
              },
              helpComponent: ProjectPlanHelp,
              key: 'projectPlanV2',
            },
          },
        ],
      },
    ],
  },
];

export const router = createRouter({
  routes,
  history: createWebHashHistory(),
});

// eslint-disable-next-line sonarjs/no-inconsistent-returns
router.beforeEach((to, from) => {
  const { isLogged } = useAuthentication();
  const { getConfig, setConfig } = useConfig();

  if (to.matched[0]?.path === '/app' && !isLogged.value) {
    if (from.fullPath === '/') {
      setConfig({ ...getConfig(), REDIRECT_URL: to.fullPath });
    }
    return false;
  }

  if (to.name) {
    const key = (to.meta as { key?: string } | undefined)?.key;
    const title = key ? i18n.global.t(`menu.${key}`) : (to.name as string);
    document.title = title as string;
  }
});

export const navigateSubmenuItem = (submenuItem: SubmenuLike) => {
  if (submenuItem.type === 'static') {
    const path = submenuItem.link ?? `/google-doc/document/${submenuItem.uuid}`;
    router.push(path).catch((e: unknown) => logger.error(e));
  } else {
    const link = `/google-doc/document/${submenuItem.uuid}`;
    router.push(link).catch((e: unknown) => logger.error(e));
  }
};

export const navigateSubmenuItemNewWindow = (submenuItem: SubmenuItem) => {
  if (submenuItem.type === 'static') {
    const route = router.resolve({ path: submenuItem.link });
    globalThis.open(route.href, '_blank');
  } else {
    const link = `/google-doc/document/${submenuItem.uuid}`;
    const route = router.resolve({ path: link });
    globalThis.open(route.href, '_blank');
  }
};
